import * as Api from '@ViewModels';
import moment from 'moment-timezone';
import { runListDateParts } from '../EmailExecBriefs/helpers';

export interface BriefDayGroup {
	key: string;
	label: string;
	full: string;
	briefs: Api.IMeetingPrepBrief[];
	preppedCount: number;
}

export const briefMeetingTime = (brief: Api.IMeetingPrepBrief, timeZone: string): string =>
	brief.meetingStartTime ? moment(brief.meetingStartTime).tz(timeZone).format('h:mm A') : '';

export const briefMeetingTitle = (brief: Api.IMeetingPrepBrief): string =>
	brief.meetingSummary?.trim() || 'Untitled meeting';

/**
 * "Christy Alvarez · Harbor Point Dental +2". Company is best-effort server-side, so it drops out
 * cleanly, and a Skipped brief carries no attendees at all.
 */
export const briefAttendeeSummary = (brief: Api.IMeetingPrepBrief): string => {
	const attendees = brief.attendees ?? [];
	const [first] = attendees;
	if (!first) {
		return '';
	}
	const name = first.displayName?.trim() || first.email?.trim() || 'Unnamed attendee';
	const company = first.companyName?.trim();
	const rest = attendees.length - 1;
	const lead = company ? `${name} · ${company}` : name;
	return rest > 0 ? `${lead} +${rest}` : lead;
};

export const groupBriefsByDay = (briefs: Api.IMeetingPrepBrief[], timeZone: string): BriefDayGroup[] => {
	const groups: BriefDayGroup[] = [];
	const byKey = new Map<string, BriefDayGroup>();

	briefs.forEach(brief => {
		const anchor = brief.meetingStartTime ?? brief.creationDate;
		if (!anchor) {
			return;
		}
		const key = moment(anchor).tz(timeZone).format('YYYY-MM-DD');
		let group = byKey.get(key);
		if (!group) {
			const parts = runListDateParts(anchor, timeZone);
			group = { key, label: parts.primary, full: parts.full, briefs: [], preppedCount: 0 };
			byKey.set(key, group);
			groups.push(group);
		}
		group.briefs.push(brief);
	});

	groups.forEach(group => {
		group.briefs.sort((a, b) => {
			const aTime = a.meetingStartTime ? new Date(a.meetingStartTime).getTime() : 0;
			const bTime = b.meetingStartTime ? new Date(b.meetingStartTime).getTime() : 0;
			return bTime - aTime;
		});
		group.preppedCount = group.briefs.filter(brief => brief.status === 'Success').length;
	});

	return groups;
};

export const firstReadableBrief = (groups: BriefDayGroup[]): Api.IMeetingPrepBrief | undefined => {
	for (const group of groups) {
		const found = group.briefs.find(brief => brief.status === 'Success');
		if (found) {
			return found;
		}
	}
	return undefined;
};
